<template>
    <div class="modal-backdrop" v-if="show" @click.self="closeModal">
        <div class="modal-content notification-modal">
            <h3>Send Event Notifications</h3>
            
            <!-- Notification Status -->
            <div v-if="notificationStatus" class="notification" :class="{
                'is-info': notificationStatus.jobs.some(job => ['pending', 'processing'].includes(job.status)),
                'is-success': notificationStatus.jobs.every(job => job.status === 'completed'),
                'is-danger': notificationStatus.jobs.some(job => job.status === 'failed')
            }">
                <p class="mb-2"><strong>{{ notificationStatus.message }}</strong></p>
                <div v-for="job in notificationStatus.jobs" :key="job.id" class="ml-4">
                    <p>
                        {{ job.job_type === 'member' ? 'Member Notifications' : 'Speaker Notifications' }}:
                        {{ job.processed_count }}/{{ job.total_count }}
                        ({{ job.status }})
                        <span v-if="job.error_message" class="has-text-danger">
                            - {{ job.error_message }}
                        </span>
                    </p>
                </div>
            </div>

            <!-- Member Notification Section -->
            <div class="notification-section">
                <div class="section-header">
                    <h4>Member Notification</h4>
                    <span class="recipient-count">{{ memberCount }} recipients</span>
                </div>
                <div class="template-preview">
                    <div class="form-group">
                        <label>Select Template:</label>
                        <select v-model="selectedMemberTemplate" class="form-select">
                            <option value="">Choose a template...</option>
                            <option 
                                v-for="template in memberTemplates" 
                                :key="template.id"
                                :value="template.id"
                            >
                                {{ template.name }}
                            </option>
                        </select>
                    </div>
                    <div class="preview-header" v-if="selectedMemberTemplate">
                        <span><strong>Subject:</strong> {{ parsedMemberSubject }}</span>
                        <button 
                            @click="previewEmail('member')" 
                            class="preview-btn"
                            :disabled="loading"
                        >
                            <i class="material-icons">{{ previewLoading ? 'hourglass_empty' : 'preview' }}</i>
                            {{ previewLoading ? 'Generating...' : 'Preview' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Speaker Notification Section -->
            <div class="notification-section" v-if="hasSpeakers">
                <div class="section-header">
                    <h4>Speaker Notifications</h4>
                    <span class="recipient-count">{{ speakerCount }} speakers</span>
                </div>

                <!-- Individual Speaker Sections -->
                <div v-for="speaker in eventSpeakers" :key="speaker.id" class="speaker-section">
                    <div class="speaker-info">
                        <div class="speaker-details-row">
                            <div class="speaker-name">
                                <i class="material-icons">person</i>
                                {{ speaker.name }}
                            </div>
                            <div class="speaker-topic">
                                {{ speaker.segmentName }}
                            </div>
                        </div>
                    </div>

                    <div class="template-preview">
                        <div class="form-group">
                            <label>Select Template for {{ speaker.name }}:</label>
                            <select 
                                :value="getSelectedTemplate(speaker)"
                                @change="handleTemplateChange(speaker, $event.target.value)"
                                class="form-select"
                            >
                                <option value="">Select a template...</option>
                                <option v-for="template in getSpeakerTemplatesForType()" 
                                        :key="template.id" 
                                        :value="template.id">
                                    {{ template.name }}
                                </option>
                            </select>
                        </div>
                        <div class="preview-header" v-if="selectedTemplates.get(speaker.sessionId)">
                            <span><strong>Subject:</strong> {{ getParsedSpeakerSubject(speaker) }}</span>
                            <button 
                                @click="previewEmail('speaker', speaker)" 
                                class="preview-btn"
                                :disabled="loading"
                            >
                                <i class="material-icons">{{ previewLoading ? 'hourglass_empty' : 'preview' }}</i>
                                {{ previewLoading ? 'Generating...' : 'Preview' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="modal-actions">
                <button 
                    @click="closeModal" 
                    class="cancel-btn"
                    :disabled="sending || sendingTest"
                >
                    Cancel
                </button>
                <button 
                    @click="showTestEmailInput" 
                    class="test-btn"
                    :disabled="!canSend || sending || sendingTest || notificationStatus"
                >
                    <i class="material-icons">{{ sendingTest ? 'hourglass_empty' : 'bug_report' }}</i>
                    <span v-if="!sendingTest">Send Test Email</span>
                    <span v-else>Sending Test...</span>
                </button>
                <button 
                    @click="sendNotifications" 
                    class="send-btn"
                    :disabled="!canSend || sending || sendingTest || notificationStatus"
                >
                    <i class="material-icons">{{ sending ? 'hourglass_empty' : 'send' }}</i>
                    <span v-if="!notificationStatus">Send Notifications</span>
                    <span v-else>Processing...</span>
                </button>
            </div>

            <!-- Test Email Modal -->
            <div class="modal-backdrop" v-if="showTestModal" @click.self="closeTestModal">
                <div class="modal-content test-email-modal">
                    <h4>Send Test Email</h4>
                    <p>Enter an email address to receive a test of the configured notifications:</p>
                    <div class="form-group">
                        <label for="testEmailInput">Test Email Address:</label>
                        <input 
                            id="testEmailInput"
                            type="email" 
                            v-model="testEmailAddress" 
                            class="form-input"
                            placeholder="your-email@example.com"
                            @keyup.enter="sendTestEmail"
                        >
                    </div>
                    <div class="test-modal-actions">
                        <button @click="closeTestModal" class="cancel-btn" :disabled="sendingTest">
                            Cancel
                        </button>
                        <button @click="sendTestEmail" class="send-btn" :disabled="!testEmailAddress || sendingTest">
                            <i class="material-icons">{{ sendingTest ? 'hourglass_empty' : 'send' }}</i>
                            {{ sendingTest ? 'Sending...' : 'Send Test' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Add notification banner -->
            <div v-if="notification" :class="['notification', notification.type]">
                {{ notification.message }}
            </div>

            <!-- Add preview modal -->
            <EmailPreviewModal
                v-if="showPreview"
                :show="showPreview"
                :type="previewType"
                :subject="previewSubject"
                :parsedBody="previewBody"
                :event="eventPreview"
                :format="eventFormat"
                :recipientCount="previewType === 'member' ? memberCount : 1"
                @close="showPreview = false"
            />

            <!-- Add loading overlay -->
            <div class="loading-overlay" v-if="loading">
                <div class="loading-spinner"></div>
                <div class="loading-text">{{ loadingMessage }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { emailService } from '../services/api';
import { meetingFormatService } from '@/services/meetingFormatService';
import { calculateSpeakerDetails, formatEventDate, formatTime, generateAgendaTable } from '../../../shared/utils/email/formatters.js';
import EmailPreviewModal from './EmailPreviewModal.vue';

export default {
    name: 'EventNotificationModal',
    components: {
        EmailPreviewModal
    },
    props: {
        show: Boolean,
        event: Object,
        members: Array,
    },
    setup(props, { emit }) {
        const sending = ref(false);
        const sendToMembers = ref(true);
        const sendToSpeakers = ref(true);
        const memberTemplate = ref(null);
        const speakerTemplate = ref(null);
        const showPreview = ref(false);
        const previewType = ref(null);
        const previewSubject = ref('');
        const previewBody = ref('');
        const notification = ref(null);
        const loading = ref(false);
        const loadingMessage = ref('');
        const previewLoading = ref(false);
        const selectedMemberTemplate = ref('');
        const selectedSpeakerTemplate = ref('');
        const memberTemplates = ref([]);
        const speakerTemplates = ref([]);
        const eventFormat = ref(null);
        const speakerTemplateSelections = ref({});
        const eventPreview = ref(null);
        const selectedTemplates = ref(new Map());
        const notificationStatus = ref(null);
        const statusCheckInterval = ref(null);
        const showTestModal = ref(false);
        const testEmailAddress = ref('');
        const sendingTest = ref(false);

        // Computed properties
        const memberCount = computed(() => props.members?.length || 0);

        const eventSpeakers = computed(() => {
            if (!props.event?.sessions) {
                console.error('No sessions found in event:', props.event);
                return [];
            }
            
            return props.event.sessions
                .filter(session => session.speaker_id && 
                    (session.session_type === 'member_speaker' || 
                     session.session_type === 'external_speaker'))
                .map(session => {
                    // Use the shared utility to calculate speaker details
                    const details = calculateSpeakerDetails(session, props.event, eventFormat.value);
                    
                    return {
                        id: session.speaker_id,
                        sessionId: session.id,
                        name: session.speaker_name || `${session.speaker_first_name} ${session.speaker_last_name}`.trim(),
                        sessionType: session.session_type,
                        segmentName: details.segmentName,
                        description: session.description || '',
                        start_time: details.startTime,
                        duration: details.duration,
                        role: session.session_type === 'member_speaker' ? 'Member Speaker' : 'External Speaker'
                    };
                });
        });

        const hasSpeakers = computed(() => eventSpeakers.value.length > 0);
        
        const speakerCount = computed(() => eventSpeakers.value.length);

        const filteredSpeakerTemplates = computed(() => {
            // Get unique session types from speakers
            const sessionTypes = [...new Set(eventSpeakers.value.map(s => s.sessionType))];
            
            // Filter templates based on session types
            return speakerTemplates.value.filter(template => {
                // If template has a specific type tag (e.g., hot_seat, workshop, education)
                if (template.tags && template.tags.length > 0) {
                    return template.tags.some(tag => sessionTypes.includes(tag));
                }
                // If no specific tags, show for all types
                return true;
            });
        });

        const canSend = computed(() => {
            // Allow sending if either:
            // 1. A member template is selected, or
            // 2. At least one speaker has a template selected
            return (selectedMemberTemplate.value || 
                    (hasSpeakers.value && Array.from(selectedTemplates.value.values()).some(id => id))) && 
                   !sending.value;
        });

        const getMemberTemplate = computed(() => 
            memberTemplates.value.find(t => t.id === selectedMemberTemplate.value)
        );
        
        const getSpeakerTemplate = computed(() => 
            speakerTemplates.value.find(t => t.id === selectedSpeakerTemplate.value)
        );

        const parsedMemberSubject = computed(() => {
            if (!getMemberTemplate.value?.subject) return '';
            return getMemberTemplate.value.subject
                .replace(/\{\{event\.date\}\}/g, formatEventDate(props.event?.event_date))
                .replace(/\{\{checkin_link\}\}/g, `${window.location.origin}/checkin/sample-preview-token`);
        });

        const parsedSpeakerSubject = computed(() => {
            if (!getSpeakerTemplate.value?.subject) return '';
            return getSpeakerTemplate.value.subject.replace(/\{\{event\.date\}\}/g, 
                formatEventDate(props.event?.event_date)
            );
        });

        // Methods
        const loadTemplates = async () => {
            try {
                const templates = await emailService.getTemplates();
                memberTemplates.value = templates.filter(t => t.type === 'member');
                speakerTemplates.value = templates.filter(t => t.type === 'speaker');
            } catch (error) {
                console.error('Error loading templates:', error);
            }
        };

        const showNotification = (message, type = 'info') => {
            notification.value = { message, type };
            setTimeout(() => {
                notification.value = null;
            }, 5000);
        };

        const loadEventFormat = async () => {
            if (props.event?.format_id) {
                try {
                    eventFormat.value = await meetingFormatService.getFormatById(props.event.format_id);
                } catch (error) {
                    console.error('Error loading event format:', error);
                }
            } else {
                console.warn('No format_id available on event');
            }
        };

        // Call loadEventFormat when modal is shown
        watch(() => props.show, (newVal) => {
            if (newVal) {
                loadEventFormat();
                loadTemplates();
            }
        }, { immediate: true });

        // Load format when event changes
        watch(() => props.event, async (newEvent) => {
            if (newEvent?.format_id) {
                try {
                    eventFormat.value = await meetingFormatService.getFormatById(newEvent.format_id);
                } catch (error) {
                    console.error('[PREVIEW] Error loading format:', error);
                }
            }
        }, { immediate: true });

        const previewEmail = async (type, speaker = null) => {
            previewLoading.value = true;
            loadingMessage.value = 'Generating preview...';
            try {
                let template;
                if (type === 'member') {
                    template = getMemberTemplate.value;
                } else {
                    const templateId = selectedTemplates.value.get(speaker.sessionId);
                    template = speakerTemplates.value.find(t => t.id === templateId);
                }
                
                if (!template) {
                    showNotification('Template not found', 'error');
                    return;
                }

                // Get first active member's name for preview
                const previewMember = props.members?.find(m => !m.deleted_at);
                const memberFirstName = previewMember?.first_name || 'Member';

 
                // Generate agenda table if format is available
                const agendaTable = eventFormat.value ? 
                    generateAgendaTable(props.event, eventFormat.value) : 
                    '(Agenda not available)';

 
                // Parse template body with all available variables
                const parsedBody = template.body.replace(/\{\{(.*?)\}\}/g, (match, key) => {
                    const trimmedKey = key.trim();
                    
                    // Handle direct variables (like checkin_link) and nested variables (like member.first_name)
                    if (trimmedKey === 'checkin_link' && type === 'member') {
                        // Return a sample check-in link for preview
                        return `${window.location.origin}/checkin/sample-preview-token-${Date.now()}`;
                    }
                    
                    const keys = trimmedKey.split('.');
                    const data = {
                        member: {
                            first_name: memberFirstName
                        },
                        event: {
                            venue_name: props.event?.venue_name,
                            venue_address: props.event?.venue_address,
                            date: formatEventDate(props.event?.event_date),
                            agenda: agendaTable
                        },
                        speaker: type === 'speaker' && speaker ? {
                            first_name: speaker.name.split(' ')[0],
                            name: speaker.name,
                            topic: speaker.segmentName,
                            description: speaker.description,
                            time: formatTime(speaker.start_time),
                            duration: `${speaker.duration} minutes`
                        } : {}
                    };

                    let value = data;
                    for (const k of keys) {
                        value = value?.[k];
                    }
                    return value || match;
                });

                previewType.value = type;
                if (type === 'member') {
                    previewSubject.value = parsedMemberSubject.value;
                } else {
                    // For speaker preview, prepare the speaker data
                    const speakerData = {
                        first_name: speaker.name.split(' ')[0],
                        name: speaker.name,
                        topic: speaker.segmentName,
                        description: speaker.description,
                        time: formatTime(speaker.start_time),
                        duration: `${speaker.duration} minutes`
                    };

                    // Create a local copy of the event for preview
                    const previewEventData = {
                        ...props.event,
                        speaker: speakerData
                    };

                    // Update the preview event ref
                    eventPreview.value = previewEventData;

                    previewSubject.value = template.subject
                        .replace(/\{\{event\.date\}\}/g, formatEventDate(previewEventData?.event_date))
                        .replace(/\{\{speaker\.first_name\}\}/g, speakerData.first_name)
                        .replace(/\{\{speaker\.topic\}\}/g, speakerData.topic)
                        .replace(/\{\{speaker\.time\}\}/g, speakerData.time);
                }
                previewBody.value = parsedBody;
                showPreview.value = true;
            } catch (error) {
                console.error('Error previewing email:', error);
                showNotification('Error generating preview', 'error');
            } finally {
                previewLoading.value = false;
            }
        };

        const sendNotifications = async () => {
            try {
                sending.value = true;
                loadingMessage.value = 'Preparing notifications...';

                // Prepare member template if selected
                let memberTemplateId = null;
                if (sendToMembers.value && selectedMemberTemplate.value) {
                    memberTemplateId = selectedMemberTemplate.value;
                }

                // Prepare speaker templates if any sessions are selected
                const speakerNotifications = [];  // Renamed to avoid confusion
                if (sendToSpeakers.value && hasSpeakers.value) {
                    for (const speaker of eventSpeakers.value) {
                        const templateId = selectedTemplates.value.get(speaker.sessionId);
                        if (!templateId) continue;

                        // Get the template from the available templates
                        const template = speakerTemplates.value.find(t => t.id === templateId);
                        if (!template) continue;

                        // Generate parsed template for this speaker
                        const parsedTemplate = {
                            subject: template.subject
                                .replace(/\{\{event\.date\}\}/g, formatEventDate(props.event?.event_date))
                                .replace(/\{\{speaker\.first_name\}\}/g, speaker.name.split(' ')[0])
                                .replace(/\{\{speaker\.topic\}\}/g, speaker.segmentName),
                            body: template.body
                        };

                        speakerNotifications.push({
                            sessionId: speaker.sessionId,
                            templateId: templateId,
                            parsedTemplate
                        });
                    }
                }

                // Queue notifications for processing
                const result = await emailService.queueNotifications(
                    props.event.id,
                    memberTemplateId,
                    speakerNotifications,  // Use the renamed variable
                    null // Don't send parsed template for members - let backend handle it
                );

                // Start checking status
                notificationStatus.value = {
                    message: 'Notifications are being processed in the background...',
                    jobs: result.jobs
                };
                startStatusCheck();

                // Show success message
                showNotification('Notifications queued successfully', 'success');
                emit('notifications-sent');
                closeModal();
            } catch (error) {
                console.error('Error queueing notifications:', error);
                showNotification('Failed to queue notifications', 'error');
            } finally {
                sending.value = false;
                loadingMessage.value = '';
            }
        };

        const checkNotificationStatus = async () => {
            try {
                const status = await emailService.getNotificationStatus(props.event.id);
                const pendingJobs = status.filter(job => ['pending', 'processing'].includes(job.status));
                
                if (pendingJobs.length === 0) {
                    // All jobs completed
                    const failedJobs = status.filter(job => job.status === 'failed');
                    if (failedJobs.length > 0) {
                        notification.value = {
                            message: 'Some notifications failed to send.',
                            type: 'error'
                        };
                    } else {
                        notification.value = {
                            message: 'All notifications sent successfully!',
                            type: 'success'
                        };
                    }
                    stopStatusCheck();
                } else {
                    // Update progress
                    const totalProcessed = status.reduce((sum, job) => sum + job.processed_count, 0);
                    const totalCount = status.reduce((sum, job) => sum + job.total_count, 0);
                    notification.value = {
                        message: `Processing notifications (${totalProcessed}/${totalCount})...`,
                        type: 'info'
                    };
                }
            } catch (error) {
                console.error('Error checking notification status:', error);
            }
        };

        const startStatusCheck = () => {
            statusCheckInterval.value = setInterval(checkNotificationStatus, 5000);
        };

        const stopStatusCheck = () => {
            if (statusCheckInterval.value) {
                clearInterval(statusCheckInterval.value);
                statusCheckInterval.value = null;
            }
        };

        const closeModal = () => {
            sendToMembers.value = true;
            sendToSpeakers.value = true;
            sending.value = false;
            emit('close');
        };

        const getSpeakerTemplatesForType = () => {
            // Return all speaker templates without filtering by session type
            return speakerTemplates.value;
        };

        const getParsedSpeakerSubject = (speaker) => {
            const templateId = selectedTemplates.value.get(speaker.sessionId);
            const template = speakerTemplates.value.find(t => t.id === templateId);
            if (!template?.subject) return '';
            
            return template.subject
                .replace(/\{\{event\.date\}\}/g, formatEventDate(props.event?.event_date))
                .replace(/\{\{speaker\.first_name\}\}/g, speaker.name.split(' ')[0]);
        };

        const handleTemplateChange = async (speaker, templateId) => {
            // Convert templateId to number since select values are strings
            const numericTemplateId = parseInt(templateId, 10);
            selectedTemplates.value.set(speaker.sessionId, numericTemplateId);
            
            // Update the subject line preview
            if (numericTemplateId) {
                const template = speakerTemplates.value.find(t => t.id === numericTemplateId);
                if (template) {
                    previewSubject.value = template.subject
                        .replace(/\{\{event\.date\}\}/g, formatEventDate(props.event?.event_date))
                        .replace(/\{\{speaker\.first_name\}\}/g, speaker.name.split(' ')[0])
                        .replace(/\{\{speaker\.topic\}\}/g, speaker.segmentName)
                        .replace(/\{\{speaker\.time\}\}/g, speaker.start_time.toLocaleTimeString('en-AU', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Australia/Brisbane'
                        }));
                }
            }
        };

        const updatePreview = async (speaker, templateId) => {
            try {
                const template = speakerTemplates.value.find(t => t.id === templateId);
                if (!template) return;

                const data = {
                    speaker: {
                        first_name: speaker.name.split(' ')[0],
                        name: speaker.name,
                        topic: speaker.segmentName,
                        description: speaker.description,
                        time: speaker.start_time.toLocaleTimeString('en-AU', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Australia/Brisbane'
                        }),
                        duration: `${speaker.duration} minutes`
                    },
                    event: {
                        date: new Date(props.event.event_date).toLocaleString('en-AU', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            timeZone: 'Australia/Brisbane'
                        }),
                        venue_name: props.event.venue_name,
                        venue_address: props.event.venue_address
                    }
                };

                // Parse template with data
                previewSubject.value = await parseTemplate(template.subject, data);
                previewBody.value = await parseTemplate(template.body, data);
            } catch (error) {
                console.error('Error updating preview:', error);
                showNotification('Failed to update preview', 'error');
            }
        };

        const parseTemplate = (template, data) => {
            return template.replace(/\{\{(.*?)\}\}/g, (match, key) => {
                const keys = key.trim().split('.');
                let value = data;
                for (const k of keys) {
                    value = value?.[k];
                }
                return value || match;
            });
        };

        const getSelectedTemplate = (speaker) => {
            return selectedTemplates.value.get(speaker.sessionId) || '';
        };

        const showTestEmailInput = () => {
            showTestModal.value = true;
        };

        const closeTestModal = () => {
            showTestModal.value = false;
            testEmailAddress.value = '';
        };

        const sendTestEmail = async () => {
            try {
                sendingTest.value = true;
                
                // Prepare member template if selected
                let memberTemplateId = null;
                if (selectedMemberTemplate.value) {
                    memberTemplateId = selectedMemberTemplate.value;
                }

                // Prepare speaker templates if any sessions are selected
                const speakerNotifications = [];
                if (hasSpeakers.value) {
                    for (const speaker of eventSpeakers.value) {
                        const templateId = selectedTemplates.value.get(speaker.sessionId);
                        if (!templateId) continue;

                        // Get the template from the available templates
                        const template = speakerTemplates.value.find(t => t.id === templateId);
                        if (!template) continue;

                        // Generate parsed template for this speaker
                        const parsedTemplate = {
                            subject: template.subject
                                .replace(/\{\{event\.date\}\}/g, formatEventDate(props.event?.event_date))
                                .replace(/\{\{speaker\.first_name\}\}/g, speaker.name.split(' ')[0])
                                .replace(/\{\{speaker\.topic\}\}/g, speaker.segmentName),
                            body: template.body
                        };

                        speakerNotifications.push({
                            sessionId: speaker.sessionId,
                            templateId: templateId,
                            parsedTemplate
                        });
                    }
                }

                await emailService.sendTestEmails(
                    testEmailAddress.value,
                    props.event.id,
                    memberTemplateId,
                    speakerNotifications
                );

                showNotification(`Test emails sent successfully to ${testEmailAddress.value}`, 'success');
                closeTestModal();
            } catch (error) {
                console.error('Error sending test emails:', error);
                showNotification('Failed to send test emails', 'error');
            } finally {
                sendingTest.value = false;
            }
        };

        return {
            sending,
            sendToMembers,
            sendToSpeakers,
            memberTemplate,
            speakerTemplate,
            memberCount,
            hasSpeakers,
            speakerCount,
            canSend,
            previewEmail,
            sendNotifications,
            closeModal,
            showPreview,
            previewType,
            previewSubject,
            previewBody,
            notification,
            loading,
            loadingMessage,
            previewLoading,
            selectedMemberTemplate,
            selectedSpeakerTemplate,
            memberTemplates,
            speakerTemplates,
            getMemberTemplate,
            getSpeakerTemplate,
            parsedMemberSubject,
            parsedSpeakerSubject,
            eventFormat,
            eventSpeakers,
            filteredSpeakerTemplates,
            speakerTemplateSelections,
            getSpeakerTemplatesForType,
            getParsedSpeakerSubject,
            eventPreview,
            selectedTemplates,
            handleTemplateChange,
            updatePreview,
            getSelectedTemplate,
            notificationStatus,
            statusCheckInterval,
            showTestModal,
            testEmailAddress,
            sendingTest,
            showTestEmailInput,
            closeTestModal,
            sendTestEmail
        };
    }
};
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
}

.notification-modal {
    max-width: 600px;
    width: 90%;
}

.notification-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.recipient-count {
    font-size: 0.9rem;
    color: #666;
}

.template-preview {
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.5rem;
}

.preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.preview-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: none;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
}

.preview-btn:hover {
    background: #f0f0f0;
}

.send-options {
    margin: 1rem 0;
}

.checkbox-container {
    display: block;
    margin: 0.5rem 0;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
}

.send-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #4CAF50;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.send-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.test-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #fd7e14;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.test-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.cancel-btn {
    background: #dc3545;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.cancel-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.notification {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 4px;
    text-align: center;
}

.notification.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.notification.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.notification.info {
    background-color: #cce5ff;
    color: #004085;
    border: 1px solid #b8daff;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1100;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text {
    margin-top: 1rem;
    color: #666;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.form-select {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.speaker-details {
    background: #f8f9fa;
    border-radius: 4px;
    padding: 1rem;
    margin-bottom: 1rem;
}

.speaker-info {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e9ecef;
}

.speaker-details-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.speaker-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    font-size: 1.1rem;
}

.speaker-topic {
    color: #4a5568;
    font-size: 0.9rem;
    padding: 0.25rem 0.75rem;
    background: #e9ecef;
    border-radius: 1rem;
}

.speaker-section {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 1rem;
    margin-bottom: 1rem;
}

.speaker-section:last-child {
    margin-bottom: 0;
}

.test-email-modal {
    max-width: 400px;
    width: 90%;
}

.form-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 0.9rem;
}

.form-input:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
}

.test-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
}
</style> 